import React from 'react'

import type { TransitionStatus } from 'react-transition-group'
import { Transition } from 'react-transition-group'
import type { EnterHandler, ExitHandler } from 'react-transition-group/Transition'

const DEFAULT_DURATION = 225

const DEFAULT_STYLE = {
  transition: `opacity var(--transition-duration, 225ms) cubic-bezier(0.4, 0, 0.2, 1)`,
  opacity: 0,
}

const TRANSITION_STYLES: Record<TransitionStatus, React.CSSProperties | undefined> = {
  entering: { opacity: 1 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0, visibility: 'hidden' },
  unmounted: undefined,
}

const STATE_TIMEOUT_MAP: Record<TransitionStatus, 'appear' | 'enter' | 'exit'> = {
  entering: 'enter',
  entered: 'enter',
  exiting: 'exit',
  exited: 'exit',
  unmounted: 'exit',
}

function getTransitionStyles(state: TransitionStatus, timeout: React.ComponentProps<typeof Transition>['timeout']) {
  const duration = typeof timeout === 'number' ? timeout : (timeout?.[STATE_TIMEOUT_MAP[state]] ?? DEFAULT_DURATION)
  return {
    ...DEFAULT_STYLE,
    ...TRANSITION_STYLES[state],
    '--transition-duration': `${duration}ms`,
  }
}

interface ChildrenProps {
  ref: React.MutableRefObject<null>
  style?: React.CSSProperties
}

export interface FadeProps {
  children: React.ReactElement<ChildrenProps>
  in: boolean
  mountOnEnter?: boolean
  onEnter?: EnterHandler<HTMLElement | undefined> | undefined
  onEntered?: EnterHandler<HTMLElement | undefined> | undefined
  onEntering?: EnterHandler<HTMLElement | undefined> | undefined
  onExit?: ExitHandler<HTMLElement | undefined> | undefined
  onExited?: ExitHandler<HTMLElement | undefined> | undefined
  onExiting?: ExitHandler<HTMLElement | undefined> | undefined
  timeout?: number | { appear?: number; enter?: number; exit?: number }
  unmountOnExit?: boolean
}

export function Fade({ children, in: inProp, timeout = DEFAULT_DURATION, ...rest }: FadeProps) {
  const nodeRef = React.useRef(null)

  return (
    <Transition nodeRef={nodeRef} in={inProp} timeout={timeout} {...rest}>
      {state => {
        // Pass the extra props to the children that is handling the fade logic.
        let childrenWithExtraProps = children
        // Checking isValidElement is the safe way and avoids a typescript error too.
        if (React.isValidElement(children)) {
          childrenWithExtraProps = React.cloneElement(children, {
            ref: nodeRef,
            style: {
              ...children.props.style,
              ...getTransitionStyles(state, timeout),
            },
          })
        }
        return childrenWithExtraProps
      }}
    </Transition>
  )
}
